import React, { useState } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/esm/Page/AnnotationLayer.css';
import 'react-pdf/dist/esm/Page/TextLayer.css';
import HashLoader from 'react-spinners/HashLoader';
import ClipLoader from 'react-spinners/ClipLoader';
import usePDFViewer from './usePDFViewer';
import './PDFViewer.css';

pdfjs.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

const BODY_PADDING = 32; // 16px each side

export default function PDFViewer({ fileUrl, fileName = 'documento.pdf' }) {
  const {
    numPages,
    currentPage,
    scale,
    containerWidth,
    isLoading,
    error,
    containerRef,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    goToPreviousPage,
    goToNextPage,
    goToPage,
    zoomIn,
    zoomOut,
    resetZoom,
  } = usePDFViewer();

  const [isPageLoading, setIsPageLoading] = useState(false);

  const handleGoToPreviousPage = () => { setIsPageLoading(true); goToPreviousPage(); };
  const handleGoToNextPage = () => { setIsPageLoading(true); goToNextPage(); };
  const handleGoToPage = (page) => { setIsPageLoading(true); goToPage(page); };

  const pageWidth = containerWidth
    ? Math.max((containerWidth - BODY_PADDING) * scale, 100)
    : undefined;

  return (
    <div className="pdf-viewer">
      <div className="pdf-viewer__toolbar">
        <div className="pdf-viewer__toolbar-group">
          <button
            className="pdf-viewer__btn"
            onClick={handleGoToPreviousPage}
            disabled={currentPage <= 1}
            title="Página anterior"
          >
            &#8592;
          </button>

          <div className="pdf-viewer__page-control">
            <input
              type="number"
              className="pdf-viewer__page-input"
              value={currentPage}
              min={1}
              max={numPages || 1}
              onChange={(e) => handleGoToPage(e.target.value)}
              aria-label="Número de página"
            />
            <span className="pdf-viewer__page-total">
              / {numPages ?? '–'}
            </span>
          </div>

          <button
            className="pdf-viewer__btn"
            onClick={handleGoToNextPage}
            disabled={!numPages || currentPage >= numPages}
            title="Página siguiente"
          >
            &#8594;
          </button>
        </div>

        <div className="pdf-viewer__toolbar-group">
          <button
            className="pdf-viewer__btn"
            onClick={zoomOut}
            disabled={scale <= 0.5}
            title="Alejar"
          >
            &#8722;
          </button>

          <button
            className="pdf-viewer__btn pdf-viewer__btn--zoom-label"
            onClick={resetZoom}
            title="Restablecer zoom"
          >
            {Math.round(scale * 100)}%
          </button>

          <button
            className="pdf-viewer__btn"
            onClick={zoomIn}
            disabled={scale >= 3.0}
            title="Acercar"
          >
            &#43;
          </button>
        </div>

        <div className="pdf-viewer__toolbar-group">
          <a
            className="pdf-viewer__btn pdf-viewer__btn--download"
            href={fileUrl}
            download={fileName}
            title="Descargar PDF"
          >
            &#8659; Descargar
          </a>
        </div>
      </div>

      <div className="pdf-viewer__body" ref={containerRef}>
        {error ? (
          <div className="pdf-viewer__error">
            <p>{error}</p>
            <a href={fileUrl} download={fileName} className="pdf-viewer__btn pdf-viewer__btn--download">
              &#8659; Descargar PDF
            </a>
          </div>
        ) : (
          <>
            {isLoading && (
              <div className="pdf-viewer__overlay">
                <HashLoader color="#f97316" size={64} />
                <p className="pdf-viewer__overlay-text">Cargando documento...</p>
              </div>
            )}

            <Document
              file={fileUrl}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={onDocumentLoadError}
              loading={null}
            >
              {!isLoading && (
                <div className="pdf-viewer__page-wrapper">
                  {isPageLoading && (
                    <div className="pdf-viewer__page-spinner">
                      <ClipLoader color="#f97316" size={32} />
                    </div>
                  )}
                  <Page
                    pageNumber={currentPage}
                    width={pageWidth}
                    renderTextLayer={true}
                    renderAnnotationLayer={true}
                    onRenderSuccess={() => setIsPageLoading(false)}
                    loading={null}
                  />
                </div>
              )}
            </Document>
          </>
        )}
      </div>
    </div>
  );
}
